import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  transactionFailed,
  transactionInit,
  transactionProcessing,
  TRANSACTION_INIT
} from '../../../../Pricing/actions'
import { LoadingBox } from '../../../LoadingBox/LoadingBox'
import { getOrCreatePaddleCustomer } from '../../../../services/paddleBilling'
import { setShowPaymentModal } from 'actions'
import * as Sentry from '@sentry/react'
import { PLANS_ID } from '../../../../constants/plans'
import { calcUpgradeTime, trackSubscriptionUpgrade, trackSubscriptionUpgradeInfo } from '../../../../utils/analytics'
import { PaddleInstance } from '../../../../utils/paddle'

import styles from '../../PaymentModal.module.css'

const getRedirectUrl = ({ customerId, isTrialUpgrade, paymentGateway, transactionId }) =>
  `/pro-welcome?id=${customerId}&skipq=${isTrialUpgrade}&txid=${transactionId}&payment=${paymentGateway}`

const isTrialUpgrade = window.location.pathname.includes('manage')

const PaddleBillingPay = ({
  transactionState,
  dispatch,
  email: emailProp,
  paddleBillingId,
  source,
  productId,
  period,
  accountCreated
}) => {
  const history = useHistory()
  const userEmail = useSelector((state) => state.manage?.userProfile?.email)
  const email = userEmail || emailProp
  const [checkoutLoader, setCheckoutLoader] = useState(true)
  const [paddle, setPaddle] = useState(null)
  const [customerId, setCustomerId] = useState(null)
  const [depsError, setDepsError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  const handleRetry = () => {
    setDepsError(null)
    setCustomerId(null)
    setPaddle(null)
    setCheckoutLoader(true)
    setRetryCount((c) => c + 1)
  }

  // Stable refs so the Paddle init useEffect doesn't re-run on every render.
  const handleCheckoutSuccess = useCallback(
    async (eventData) => {
      try {
        dispatch(transactionProcessing())
        // Extract transaction ID from checkout event
        const { transaction_id: transactionId, customer } = eventData
        const customerId = customer?.id || ''
        // Track analytics
        trackSubscriptionUpgrade(
          email,
          PLANS_ID[productId]?.label,
          PLANS_ID[productId]?.prices[period]?.stripe?.usd?.value,
          period,
          transactionId,
          source
        )
        trackSubscriptionUpgradeInfo(email, calcUpgradeTime(accountCreated))

        dispatch(transactionInit())
        dispatch(setShowPaymentModal({ showPaymentModal: false, upgradePlanId: null }))
        history.push(getRedirectUrl({ customerId, isTrialUpgrade, transactionId, paymentGateway: 'PADDLE_BILLING' }))
      } catch (error) {
        console.error('Failed to get subscription details after checkout:', error)
        dispatch(transactionFailed())
        Sentry.captureException(error, {
          tags: { section: 'paddle-billing-subscription-creation-error' }
        })
      }
    },
    [dispatch, email, productId, period, source, accountCreated, history]
  )

  const handleCheckoutClose = useCallback(() => {
    dispatch(transactionInit())
  }, [dispatch])

  // Fetch or create the Paddle customer once on mount (and on retry).
  useEffect(() => {
    getOrCreatePaddleCustomer(email)
      .then(({ data }) => setCustomerId(data.customerId))
      .catch((error) => {
        console.error('Failed to get Paddle customer:', error)
        Sentry.captureException(error, { tags: { section: 'paddle-billing-get-customer-error' } })
        setDepsError("We couldn't initialise the checkout. Please try again.")
      })
  }, [retryCount])

  useEffect(() => {
    PaddleInstance()
      .then((paddleInstance) => {
        if (!paddleInstance) {
          throw new Error('Failed to initialize Paddle SDK')
        }

        // Attach checkout handlers to the paddle instance.
        // These will be called by the global eventCallback in paddle.js
        paddleInstance._checkoutSuccessHandler = handleCheckoutSuccess
        paddleInstance._checkoutCloseHandler = handleCheckoutClose

        setPaddle(paddleInstance)
      })
      .catch((error) => {
        console.error('Failed to initialize Paddle SDK:', error)
        Sentry.captureException(error, { tags: { section: 'paddle-billing-init-error' } })
        setDepsError("We couldn't initialise the checkout. Please try again.")
      })
  }, [handleCheckoutSuccess, handleCheckoutClose, retryCount])

  useEffect(() => {
    if (transactionState === TRANSACTION_INIT && paddle && customerId) {
      openCheckout()
    }
  }, [transactionState, paddle, customerId])

  useEffect(() => {
    if (!checkoutLoader && paddle && paddleBillingId) {
      paddle.Checkout.updateItems([
        {
          priceId: paddleBillingId
        }
      ])
    }
  }, [paddleBillingId, paddle, checkoutLoader])

  const openCheckout = () => {
    try {
      if (!paddle) {
        throw new Error('Paddle SDK not initialized')
      }
      if (!paddleBillingId) {
        throw new Error('Price ID not available')
      }
      if (!customerId) {
        throw new Error('Paddle customer ID not available')
      }

      paddle.Checkout.open({
        items: [{ priceId: paddleBillingId }],
        customer: { id: customerId },
        settings: {
          displayMode: 'inline',
          frameTarget: 'checkout-container',
          frameInitialHeight: '450',
          frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
          showAddTaxId: false,
          allowLogout: false
        }
      })

      setCheckoutLoader(false)
    } catch (error) {
      console.error('Failed to open Paddle checkout:', error)
      Sentry.captureException(error, { tags: { section: 'paddle-billing-checkout-error' } })
      setDepsError("We couldn't initialise the checkout. Please try again.")
    }
  }

  if (depsError) {
    return (
      <div className={`${styles.paddleContainer} d-flex flex-column align-items-center justify-content-center`}>
        <p className="text-muted mb-3">{depsError}</p>
        <button className="btn btn-secondary btn-sm" onClick={handleRetry}>
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className={styles.paddleContainer}>
      <LoadingBox className={checkoutLoader ? 'd-block' : 'd-none'} message="Loading Paddle Checkout" />
      <div className="checkout-container" />
    </div>
  )
}

export default PaddleBillingPay
